// The look, app-side (LOOKS.md): per person, stored as an id, stamped on <html>
// as data-look so a look is a stylesheet keyed off the attribute — the same
// mechanism as the detail level, which is the one preference that already
// works end to end.
//
// Its own localStorage key for the detail level's reason: a visitor in the
// tutorial has no session and still gets to choose.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { DEFAULT_LOOK, type LookId } from '@fsp/theme';
import { lookOf, parseLook } from '../looks/registry';
import { storageKey } from './storage';

export const LOOK_KEY = storageKey('look.v1');

export function readStoredLook(): LookId {
  try {
    const raw = localStorage.getItem(LOOK_KEY);
    const look = parseLook(raw);
    // A look this build no longer has reads as the default — and is stored as
    // it, or first paint would stamp the retired id and skip the cached theme
    // on every load (index.html compares the two).
    if (raw !== null && raw !== look) localStorage.setItem(LOOK_KEY, look);
    return look;
  } catch {
    return DEFAULT_LOOK;
  }
}

/** `apps/web/index.html` does the same before React mounts; keep them in sync. */
export function stampLook(look: LookId): void {
  document.documentElement.setAttribute('data-look', look);
  loadLookFonts(look);
}

/**
 * A look's own web fonts, asked for the first time the look is used or its
 * sample is shown on Appearance, and never otherwise (its folder's `fonts`, B-202):
 * somebody on Clean who never opens Appearance never downloads Crayon's
 * hands. Each family in a look's stylesheet names a fallback, so the page
 * reads while they arrive. The files are the site's own (DIRECTION D12), each
 * look's a chunk of the build that its loader brings in.
 */
export function loadLookFonts(look: LookId): void {
  const load = lookOf(look).fonts;
  if (load === null || fontsAsked.has(look)) return;
  fontsAsked.add(look);
  // A chunk that fails to arrive (offline, a deploy in between) leaves the
  // fallbacks drawing, and the next time the look is used asks again.
  load().catch(() => fontsAsked.delete(look));
}

/** The looks whose fonts have been asked for in this page. */
const fontsAsked = new Set<LookId>();

interface LookContextValue {
  look: LookId;
  /** Applied at once: the account menu's Look item and the Appearance screen. */
  setLook: (look: LookId) => void;
}

const LookContext = createContext<LookContextValue | null>(null);

export function LookProvider({ children }: { children: ReactNode }) {
  const [look, setState] = useState<LookId>(readStoredLook);

  useEffect(() => {
    stampLook(look);
  }, [look]);

  const setLook = useCallback((next: LookId) => {
    setState(next);
    try {
      localStorage.setItem(LOOK_KEY, next);
    } catch {
      // Storage blocked: the choice holds for this page load.
    }
  }, []);

  const value = useMemo(() => ({ look, setLook }), [look, setLook]);
  return <LookContext.Provider value={value}>{children}</LookContext.Provider>;
}

/**
 * A part of the page drawn in another look than the page's: the Appearance
 * screen's samples. Icons and art inside it are the scope's look's; its
 * stylesheet reaches it through the `data-look` the caller puts on it.
 */
export function LookScope({ look, children }: { look: LookId; children: ReactNode }) {
  const outer = useContext(LookContext);
  const value = useMemo(
    () => ({ look, setLook: outer?.setLook ?? (() => undefined) }),
    [look, outer],
  );
  return <LookContext.Provider value={value}>{children}</LookContext.Provider>;
}

/** The current look. Outside a provider (a test, a portal) it is the default. */
export function useLook(): LookId {
  return useContext(LookContext)?.look ?? DEFAULT_LOOK;
}

export function useLookContext(): LookContextValue {
  const ctx = useContext(LookContext);
  if (!ctx) throw new Error('useLookContext must be used inside LookProvider');
  return ctx;
}
